import { inject, InjectionToken } from '@angular/core';
import { LoginDto, SignedToken } from '@shared/modules/auth/models/auth.models';
import { AuthApi } from '@shared/modules/auth/services/auth.api';
import { AuthStorage } from '@shared/modules/auth/store/auth.storage';
import { signalStore, withMethods, withState, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { ObservableInput, pipe, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { AUTH_RC, AUTHORIZED_RC } from '@shared/navigation/constants/routes.constants';

type AuthState = Pick<SignedToken, 'accessToken' | 'refreshToken'>;

const AUTH_STATE = new InjectionToken<AuthState>('AuthState', {
  factory: () => {
    const storage = inject(AuthStorage);
    return storage.getTokens();
  },
});

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(() => inject(AUTH_STATE)),
  withMethods((store, auth = inject(AuthApi), storage = inject(AuthStorage), router = inject(Router)) => ({
    login: rxMethod<LoginDto>(
      pipe(
        switchMap((creds) =>
          auth.login(creds).pipe(
            tapResponse({
              next: (creds: SignedToken) => {
                storage.setTokens(creds);
                patchState(store, creds);
                router.navigate([AUTHORIZED_RC.root]);
              },
              error: console.error,
            })
          )
        )
      )
    ),
    logout: rxMethod(
      pipe(
        switchMap<void, ObservableInput<unknown>>(() => {
          const { accessToken, refreshToken } = storage.getTokens();
          if (!accessToken || !refreshToken) {
            return router.navigate([AUTH_RC.root]);
          }

          return auth.logout().pipe(
            tapResponse({
              next: () => {
                storage.setTokens({ accessToken: '', refreshToken: '' });
                patchState(store, { accessToken: '', refreshToken: '' });
                router.navigate([AUTH_RC.root]);
              },
              error: (err) => {
                console.error(err);
                storage.setTokens({ accessToken: '', refreshToken: '' });
                patchState(store, { accessToken: '', refreshToken: '' });
                router.navigate([AUTH_RC.root]);
              },
            })
          );
        })
      )
    ),
  }))
);
